import { Link, useParams } from "react-router-dom";
import type { Special } from "@/types";
import { useSeo } from "@/hooks/useSeo";
import { getSpecialById, getTitlesByIds } from "@/lib/titles";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TitleCard from "@/components/feature/TitleCard";
import Reveal from "@/components/base/Reveal";

export default function SpecialDetail() {
  const { id } = useParams<{ id: string }>();
  const special = getSpecialById(id);

  useSeo({
    title: special
      ? `${special.name} - WW我的快乐在哪里影视专题`
      : "专题不存在 - WW我的快乐在哪里",
    description: special ? special.intro : "抱歉，未找到对应的影视专题。",
    keywords: special
      ? `${special.name},影视专题,片单推荐,WW我的快乐在哪里`
      : "WW我的快乐在哪里",
    path: `https://mxbdsen.cn/special/${id ?? ""}`,
  });

  if (!special) {
    return (
      <section className="mx-auto flex w-full max-w-page flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-emotion-sad-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-6 text-2xl font-bold text-foreground-950">这个专题不存在</h1>
        <p className="mt-3 text-sm text-foreground-600">可能链接已失效，返回专题列表看看其他合集吧。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/special"
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
          >
            <i className="ri-stack-line" aria-hidden="true" />
            返回专题列表
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </section>
    );
  }

  const items = getTitlesByIds(special.titleIds);
  const related = special.relatedIds
    .map((rid) => getSpecialById(rid))
    .filter((item): item is Special => Boolean(item));

  return (
    <>
      <section className="relative h-[300px] w-full overflow-hidden bg-primary-950 md:h-[400px]">
        <img
          src={special.cover}
          alt={`${special.name} 专题主图`}
          title={`${special.name} - WW我的快乐在哪里影视专题`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-page px-4 pb-8 md:px-6 md:pb-10">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
              <i className="ri-bookmark-3-line" aria-hidden="true" />
              {special.tag}
            </span>
            <h1 className="mt-4 text-3xl font-black text-background-50 md:text-4xl">{special.name}</h1>
            <p className="mt-3 max-w-2xl text-sm text-background-200 md:text-base">{special.intro}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "专题合集", to: "/special" }, { label: special.name }]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_280px]">
          <div>
            <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
              专题介绍
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {special.description}
            </p>

            <h2 className="mt-10 flex items-center gap-2.5 text-xl font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-secondary-500" aria-hidden="true" />
              专题片单（{items.length} 部）
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((item, index) => (
                <Reveal key={item.id} delay={index * 50}>
                  <TitleCard item={item} className="h-full" />
                </Reveal>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-bold text-accent-800">
                <i className="ri-lightbulb-flash-line" aria-hidden="true" />
                推荐语
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-accent-900">{special.recommend}</p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="text-sm font-bold text-foreground-950">相关专题推荐</h3>
              <ul className="mt-4 space-y-3">
                {related.map((rel) => (
                  <li key={rel.id}>
                    <Link to={`/special/${rel.id}`} className="group flex items-center gap-3">
                      <span className="h-14 w-20 flex-none overflow-hidden rounded-md bg-background-200">
                        <img
                          src={rel.cover}
                          alt={`${rel.name} 专题主图`}
                          title={`${rel.name} - WW我的快乐在哪里影视专题`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {rel.name}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">
                          {rel.titleIds.length} 部作品
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}